'use client'

import {createContext, useContext, useState, useCallback} from 'react'
import debounce from 'lodash/debounce'
import {updateFavGameAction} from '@app/components/gameplayer-page/actions-section/buttons/favourite-btn/update-fav-action'

type UserGamePreference = {
  favourite: string[]
  lastPlayed: string[]
}

type UserGamePreferenceContextType = {
  userPreference: UserGamePreference
  addFav: (gameCode: string) => void
  removeFav: (gameCode: string) => void
  addLastPlayed: (gameCode: string) => void
  removeLastPlayed: (gameCode: string) => void
}

const UserGamePreferenceContext = createContext<UserGamePreferenceContextType | undefined>(undefined)

export const UserGamePreferenceProviderClient: React.FC<{
  children: React.ReactNode
  initialPreference: UserGamePreference
}> = ({children, initialPreference}) => {
  const [userPreference, setUserPreference] = useState<UserGamePreference>(initialPreference)

  const debouncedUpdateFav = useCallback(
    debounce(async (gameCode: string, isFav: boolean) => {
      try {
        const response = await updateFavGameAction({gameCode, isFav})
        if (response?.error) {
          console.error('Failed to update favorite status')
        }
      } catch (error) {
        console.error('Error updating favorite status:', error)
      }
    }, 200),
    [],
  )

  const addFav = (gameCode: string) => {
    setUserPreference((prev) => {
      const updatedPreference = {
        ...prev,
        favourite: prev.favourite.includes(gameCode) ? prev.favourite : [...prev.favourite, gameCode],
      }
      debouncedUpdateFav(gameCode, true)
      return updatedPreference
    })
  }

  const removeFav = (gameCode: string) => {
    setUserPreference((prev) => {
      const updatedPreference = {
        ...prev,
        favourite: prev.favourite.filter((game) => game !== gameCode),
      }
      debouncedUpdateFav(gameCode, false)
      return updatedPreference
    })
  }

  const addLastPlayed = (gameCode: string) => {
    setUserPreference((prev) => {
      const updatedPreference = {
        ...prev,
        lastPlayed: [gameCode, ...prev.lastPlayed.filter((g) => g !== gameCode)],
      }
      return updatedPreference
    })
  }

  const removeLastPlayed = (gameCode: string) => {
    setUserPreference((prev) => {
      const updatedPreference = {
        ...prev,
        lastPlayed: prev.lastPlayed.filter((game) => game !== gameCode),
      }
      return updatedPreference
    })
  }
  return (
    <UserGamePreferenceContext.Provider value={{userPreference, addFav, removeFav, addLastPlayed, removeLastPlayed}}>
      {children}
    </UserGamePreferenceContext.Provider>
  )
}

export const useUserGamePreference = () => {
  const context = useContext(UserGamePreferenceContext)
  if (!context) {
    throw new Error('useUserGamePreference must be used within a UserGamePreferenceProvider')
  }
  return context
}
